import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ArrowUpRight, TrendingUp, CheckSquare, CalendarClock, AlertOctagon, Users as UsersIcon, Phone, PartyPopper } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line,
} from "recharts";

const STAGE_LABEL = {
  new_lead: "New Lead", lead: "Lead", qualified: "Qualified", proposal: "Proposal",
  negotiation: "Negotiation", won: "Won", lost: "Lost",
};

const currency = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

/** Compact INR label for chart axes: ₹1.2Cr / ₹85L / ₹40k. */
const inrCompact = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 1)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(n % 1e5 === 0 ? 0 : 1)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(n % 1e3 === 0 ? 0 : 1)}k`;
  return `₹${n}`;
};

const formatStage = (key) =>
  STAGE_LABEL[key] || (key ? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

function KpiCard({ label, value, hint, icon: Icon, testid, tone }) {
  const toneStyles = {
    default: "",
    danger: "border-destructive/40",
    warn: "border-orange-400/50",
    success: "border-emerald-500/40",
  };
  return (
    <div
      data-testid={testid}
      className={`card-flat p-5 flex flex-col gap-3 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200 ${toneStyles[tone] || ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
      </div>
      <div className="text-3xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });
  const { data: feed = [] } = useQuery({
    queryKey: ["dashboard-activity"],
    queryFn: async () => (await api.get("/dashboard/activity")).data,
  });

  if (!stats) {
    return <div className="p-4 md:p-8 text-sm text-muted-foreground">Loading dashboard…</div>;
  }

  const pipelineData = stats.pipeline_by_stage.map((p) => ({
    stage: formatStage(p.stage),
    value: p.value, count: p.count,
  }));

  return (
    <div className="p-4 md:p-8 space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow mb-2">Overview</div>
          <h1 className="text-3xl tracking-tight font-medium">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Your pipeline at a glance.</p>
        </div>
      </div>

      {/* Revenue / pipeline row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard testid="kpi-revenue" label="Revenue (won)" value={currency(stats.revenue)}
          hint={`${stats.deals_won} deals closed won`} icon={TrendingUp} />
        <KpiCard testid="kpi-pipeline" label="Pipeline value" value={currency(stats.pipeline_value)}
          hint={`${stats.deals_open} open deals`} icon={ArrowUpRight} />
        <KpiCard testid="kpi-conversion" label="Win rate" value={`${stats.conversion_rate}%`}
          hint="won / (won + lost)" />
        <KpiCard testid="kpi-tasks" label="Open tasks" value={stats.open_tasks}
          hint={`${stats.contacts_count} contacts · ${stats.companies_count} companies`} icon={CheckSquare} />
      </div>

      {/* Activity KPI row */}
      <div>
        <div className="eyebrow mb-3">Activity pulse</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiCard
            testid="kpi-due-today"
            label="Due today"
            value={stats.activities_due_today}
            icon={CalendarClock}
          />
          <KpiCard
            testid="kpi-overdue"
            label="Overdue"
            value={stats.overdue_activities}
            icon={AlertOctagon}
            tone={stats.overdue_activities > 0 ? "danger" : "default"}
          />
          <KpiCard
            testid="kpi-meetings-week"
            label="Meetings this week"
            value={stats.meetings_this_week}
            icon={UsersIcon}
          />
          <KpiCard
            testid="kpi-calls-week"
            label="Calls this week"
            value={stats.calls_this_week}
            icon={Phone}
          />
          <KpiCard
            testid="kpi-completed-month"
            label="Completed this month"
            value={stats.completed_activities_this_month}
            icon={PartyPopper}
            tone="success"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-flat p-5 lg:col-span-2" data-testid="chart-pipeline">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow">Pipeline by stage</div>
              <div className="text-lg font-medium tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>Value distribution</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipelineData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 6% 90%)" vertical={false} />
                <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "hsl(240 3% 44%)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(240 3% 44%)" }} axisLine={false} tickLine={false} tickFormatter={inrCompact} />
                <Tooltip
                  cursor={{ fill: "hsl(222 100% 33% / 0.06)" }}
                  contentStyle={{ borderRadius: 8, border: "1px solid hsl(240 6% 90%)", fontSize: 12 }}
                  formatter={(v, k) => (k === "value" ? currency(v) : v)}
                />
                <Bar dataKey="value" fill="hsl(222 100% 33%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-5" data-testid="chart-revenue">
          <div className="eyebrow">Last 6 months</div>
          <div className="text-lg font-medium tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>Revenue trend</div>
          <div className="h-64 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.revenue_series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 6% 90%)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(240 3% 44%)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(240 3% 44%)" }} axisLine={false} tickLine={false} tickFormatter={inrCompact} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid hsl(240 6% 90%)", fontSize: 12 }} formatter={(v) => currency(v)} />
                <Line type="monotone" dataKey="revenue" stroke="hsl(222 100% 33%)" strokeWidth={2} dot={{ r: 3, fill: "hsl(222 100% 33%)" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Activity feed */}
      <div className="card-flat p-5" data-testid="activity-feed">
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">Recent activity</div>
        </div>
        <ul className="divide-y divide-border -mx-5">
          {feed.length === 0 && <li className="px-5 py-4 text-sm text-muted-foreground">No activity yet.</li>}
          {feed.map((item) => (
            <li key={`${item.kind}-${item.at}-${item.title}`} className="px-5 py-3 flex items-center gap-4 hover:bg-accent/40 transition-colors duration-150">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold w-20 text-muted-foreground">{item.kind}</div>
              <div className="flex-1 text-sm truncate">{item.title}</div>
              <div className="text-xs text-muted-foreground">{item.kind === "deal" ? formatStage(item.meta) : (item.meta || "")}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
